import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiUrl } from '../../config/api';
import axios from 'axios';

export interface TeamMember {
  id: number;
  user_id?: number;
  team_id?: number;
  role: string;
  skills?: string;
  user_email?: string;
  user_name?: string; // display name derived from email on server
  created_at: string;
  updated_at: string;
}

interface TeamMemberFormData {
  // For adding we only need email (invite). For editing we allow role and skills.
  email?: string;
  role?: string;
  skills?: string;
}

const TeamMembers: React.FC = () => {
  const { user, currentTeam } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState<TeamMemberFormData>({
    email: '',
    role: '',
    skills: ''
  });

  // Check if current user is admin or manager
  const isAdmin = React.useMemo(() => {
    if (!user || !members.length) return false;
    const currentUserMember = members.find(member => member.user_email === user.email);
    return currentUserMember?.role === 'admin' || currentUserMember?.role === 'manager';
  }, [user, members]);
  const fetchMembers = React.useCallback(async () => {
    try {
      setLoading(true);
      if (!user) {
        setError('User not authenticated');
        setMembers([]);
        return;
      }

      const effectiveTeamId = currentTeam?.id;
      if (!effectiveTeamId) {
        setError('Please select a team');
        setMembers([]);
        return;
      }

      const response = await axios.get(apiUrl(`/api/team-members/team/${effectiveTeamId}`));
      setMembers(response.data);
    } catch (err) {
      setError('Failed to load team members');
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTeam, user]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleAddMember = () => {
    setEditingMember(null);
    setFormData({ email: '', role: '', skills: '' });
    setDialogOpen(true);
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      email: member.user_email || '',
      role: member.role,
      skills: member.skills || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteMember = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await axios.delete(apiUrl(`/api/team-members/${id}`));
        fetchMembers();
      } catch (err) {
        setError('Failed to delete team member');
        console.error('Error deleting member:', err);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const teamId = currentTeam?.id;

      if (!teamId) {
        setError('No team selected');
        return;
      }

      if (editingMember) {
        // Only send role and skills to update endpoint
        await axios.put(apiUrl(`/api/team-members/${editingMember.id}`), {
          role: formData.role,
          skills: formData.skills
        });
      } else {
        // Create by sending an invitation via teams API
        if (!formData.email) {
          setError('Email is required to invite a new member');
          return;
        }

        await axios.post(apiUrl(`/api/teams/${teamId}/invite`), {
          email: formData.email
        });
      }
      setDialogOpen(false);
      fetchMembers();
    } catch (err) {
      setError('Failed to save team member');
      console.error('Error saving member:', err);
    }
  };

  const handleFormChange = (field: keyof TeamMemberFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Team Members
          {!isAdmin && (
            <Chip 
              label="View Only" 
              size="small" 
              color="info" 
              sx={{ ml: 2 }} 
            />
          )}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
          >
            Add Team Member
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Added</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No team members yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {isAdmin 
                        ? "Add your first team member to get started with strategy planning"
                        : "Only admins can add team members"
                      }
                    </Typography>
                    {isAdmin && (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddMember}>
                        Add First Member
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="medium">
                          {member.user_name || member.user_email || 'Unknown'}
                      </Typography>
                        {user?.email === member.user_email && (
                        <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </TableCell>
                    <TableCell>{member.role}</TableCell>
                    <TableCell>{member.user_email || '-'}</TableCell>
                  <TableCell>
                    {new Date(member.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {(isAdmin || user?.email === member.user_email) && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditMember(member)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {isAdmin && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMember(member.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Team Member' : 'Invite New Team Member'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingMember ? (
              <>
                <TextField
                  label="Role/Position"
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  fullWidth
                  required
                  placeholder="e.g., admin, member"
                />
                <TextField
                  label="Skills (comma separated)"
                  value={formData.skills}
                  onChange={(e) => handleFormChange('skills', e.target.value)}
                  fullWidth
                  placeholder="e.g., UX, React, Strategy"
                />
              </>
            ) : (
              <>
                <TextField
                  label="Email Address (BFH email required)"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  fullWidth
                  required
                  placeholder="invitee@bfh.ch"
                />
                <Typography variant="body2" color="text.secondary">
                  Invites are sent by email. The recipient must accept to become a team member.
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={editingMember ? !formData.role : !formData.email}
          >
            {editingMember ? 'Update' : 'Add'} Member
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeamMembers;